import { useId, useRef, useState } from "react";
import { uploadImage } from "../api";

const ALLOWED = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const MAX_BYTES = 5 * 1024 * 1024;

type Shape = "circle" | "square";

export function AvatarUploader({
  value,
  onChange,
  kind,
  scopeId,
  fallback,
  shape = "circle",
  label = "Profile photo",
  hint,
  testid = "avatar-uploader",
}: {
  value: string | null;
  onChange: (url: string | null) => void;
  kind: "avatars" | "business-logos" | "perk-images";
  scopeId: string;
  fallback?: string;
  shape?: Shape;
  label?: string;
  hint?: string;
  testid?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const labelId = useId();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initial = (fallback?.[0] ?? "?").toUpperCase();
  const rounded = shape === "circle" ? "rounded-full" : "rounded-lg";

  async function pick(file: File) {
    setError(null);
    if (!ALLOWED.includes(file.type)) {
      setError("Use a JPG, PNG, WebP, or GIF.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError("Max 5 MB.");
      return;
    }
    setUploading(true);
    try {
      const { url } = await uploadImage(file, kind, scopeId);
      onChange(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div className="grid gap-2">
      <span id={labelId} className="text-xs font-semibold uppercase tracking-wider text-muted">
        {label}
      </span>
      <div className="flex items-start gap-3">
        <div
          className={`shrink-0 h-16 w-16 ${rounded} border border-border bg-paper overflow-hidden flex items-center justify-center`}
          aria-labelledby={labelId}
        >
          {value ? (
            <img
              src={value}
              alt=""
              className="h-full w-full object-cover"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <span className="text-2xl font-semibold text-muted" aria-hidden>
              {initial}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn text-sm"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid={`${testid}-pick`}
            >
              {uploading ? "Uploading…" : value ? "Replace" : "Upload photo"}
            </button>
            {value && (
              <button
                type="button"
                className="btn-ghost text-sm"
                onClick={() => onChange(null)}
                disabled={uploading}
                data-testid={`${testid}-remove`}
              >
                Remove
              </button>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept={ALLOWED.join(",")}
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) pick(f);
            }}
            data-testid={`${testid}-input`}
          />
          {hint && !error && <p className="mt-1.5 text-xs text-muted">{hint}</p>}
          {error && (
            <p className="mt-1.5 text-xs text-rose" data-testid={`${testid}-error`}>
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
